import React from "react";
import { Badge } from "@/components/ui/badge";
import { Stage } from "@/types/types";
import { EvePipelinePhase } from "@/types/eve";
import { resolveStatusItem } from "@/constants/statuses";
import { customStatusKind } from "@/lib/eve";
import { CircleDashedIcon } from "lucide-react";
import { cn } from "@/lib/utils";

type BadgeSize = "sm" | "md" | "lg";

interface BadgeStatusProps {
  status: string;
  stages?: Stage[];
  /** Amy-v2 pipeline — resolves custom x-<slug> statuses to their phase labels. */
  pipeline?: EvePipelinePhase[] | null;
  size?: BadgeSize;
  showIcon?: boolean;
  showDescription?: boolean;
  extraInfo?: string;
  className?: string;
}

const sizeVariants: Record<
  BadgeSize,
  { container: string; icon: string; text: string; description: string }
> = {
  sm: {
    container: "px-1.5 py-0.5 text-xs rounded-sm",
    icon: "[&>svg]:size-3",
    text: "gap-1",
    description: "text-[10px]",
  },
  md: {
    container: "px-2 py-1 text-sm",
    icon: "[&>svg]:size-3.75",
    text: "gap-1.75",
    description: "text-[11px]",
  },
  lg: {
    container: "px-3 py-1.5 text-base",
    icon: "[&>svg]:size-5",
    text: "gap-2",
    description: "text-xs",
  },
};

export const BadgeStatus: React.FC<BadgeStatusProps> = ({
  status,
  stages,
  pipeline,
  size = "md",
  showIcon = true,
  showDescription = true,
  extraInfo,
  className,
}) => {
  const statusItem = resolveStatusItem(status, stages, pipeline);
  const sizeClasses = sizeVariants[size];
  const isCustomStatus = stages?.some((s) => s.id === status) || customStatusKind(status) !== null;

  if (!statusItem) {
    return (
      <Badge
        className={cn(
          "bg-muted text-muted-foreground cursor-default max-w-full truncate",
          sizeClasses.container,
          className,
        )}
      >
        {showIcon && (
          <CircleDashedIcon
            className={cn(sizeClasses.icon.replace("[&>svg]:", ""), "mr-1")}
          />
        )}
        {status}
      </Badge>
    );
  }

  return (
    <div
      className={cn(
        statusItem.color,
        "group rounded-md font-medium w-fit max-w-full overflow-hidden cursor-default",
        showDescription && "cursor-help",
        isCustomStatus && "border border-dashed border-current/70 bg-background",
        sizeClasses.container,
        className,
      )}
    >
      <div
        className={cn(
          "flex items-center whitespace-nowrap min-w-0",
          sizeClasses.icon,
          sizeClasses.text,
        )}
      >
        {showIcon && statusItem.icon}
        <span className="truncate" title={statusItem.name}>
          {statusItem.name}
        </span>
      </div>
      {showDescription && (statusItem.description || extraInfo) && (
        <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-250 ease-out">
          <div className="overflow-hidden max-w-0 group-hover:max-w-xs transition-[max-width] duration-250 ease-out">
            {statusItem.description && (
              <p
                className={cn(
                  "opacity-90 whitespace-nowrap truncate font-normal",
                  sizeClasses.description,
                )}
                title={statusItem.description}
              >
                {statusItem.description}
              </p>
            )}
            {extraInfo && (
              <p
                className={cn(
                  "whitespace-nowrap truncate font-normal",
                  sizeClasses.description,
                )}
                title={extraInfo}
              >
                {extraInfo}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export { STATUS_MAP as statusMap } from "@/constants/statuses";
